import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"
import { getMentorCoursesAnalyticsApi, MentorCoursesAnalytics } from "../../features/analytics/api/mentorAnalytics.api"
import { HBarChart, VBarChart, ChartCard } from "../analytics/Charts"
import { ChevronRightIcon, ExclamationTriangleIcon, AcademicCapIcon } from "@heroicons/react/16/solid"

const Stat = ({ label, value, sub }: { label: string; value: string | number; sub?: string }) => (
  <div className="bg-white border border-gray-100 rounded-2xl px-5 py-4">
    <p className="text-xs text-gray-400 mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-900">
      {typeof value === "number" ? value.toLocaleString("ru") : value}
    </p>
    {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
  </div>
)

const Stars = ({ rating }: { rating: number }) => (
  <span className="inline-flex items-center gap-0.5">
    {Array.from({ length: 5 }).map((_, i) => (
      <span key={i} className={`text-[11px] ${i < Math.floor(rating) ? "text-amber-400" : "text-gray-200"}`}>★</span>
    ))}
    <span className="ml-1 text-xs text-gray-500">{rating}</span>
  </span>
)

export const MentorAnalyticsCoursesPage = () => {
  const navigate = useNavigate()
  const [data, setData] = useState<MentorCoursesAnalytics | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  const load = () => {
    setLoading(true)
    setError(false)
    getMentorCoursesAnalyticsApi()
      .then((res) => setData(res.data))
      .catch(() => setError(true))
      .finally(() => setLoading(false))
  }

  useEffect(() => { load() }, [])

  if (error) {
    return (
      <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
        <ExclamationTriangleIcon className="w-8 h-8 text-red-200 mx-auto mb-2" />
        <p className="text-sm text-gray-400 mb-2">Не удалось загрузить курсы</p>
        <button onClick={load} className="text-xs font-medium text-blue-600 hover:text-blue-700 underline">
          Повторить
        </button>
      </div>
    )
  }

  if (loading || !data) {
    return (
      <div className="space-y-5">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {[1, 2, 3, 4].map((i) => <div key={i} className="bg-white border border-gray-100 rounded-2xl h-20 animate-pulse" />)}
        </div>
        <div className="bg-white rounded-2xl border border-gray-100 h-64 animate-pulse" />
      </div>
    )
  }

  return (
    <div className="space-y-5">
      {/* Summary cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Stat label="Просмотров"       value={data.total_views} />
        <Stat label="Покупок"          value={data.total_purchases} />
        <Stat label="Выручка"          value={`${(data.total_revenue / 1000).toFixed(0)}K сом`} />
        <Stat label="Ср. завершаемость" value={`${data.avg_completion_rate}%`} />
      </div>

      {data.courses.length === 0 ? (
        <div className="bg-white border border-gray-100 rounded-2xl py-16 text-center">
          <AcademicCapIcon className="w-8 h-8 text-gray-200 mx-auto mb-2" />
          <p className="text-sm text-gray-400">Курсов пока нет</p>
        </div>
      ) : (
        <>
          {/* Charts */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <ChartCard title="Просмотры по курсам">
              <HBarChart
                data={data.courses.map((c) => ({ label: c.title.slice(0, 20), value: c.views }))}
                color="#3b82f6"
              />
            </ChartCard>
            <ChartCard title="Выручка по курсам (сом)">
              <VBarChart
                data={data.courses.map((c) => ({ label: c.title.slice(0, 12), value: c.revenue }))}
                color="#8b5cf6"
              />
            </ChartCard>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <div className="px-5 py-4 border-b bg-gray-50/60">
              <h3 className="font-semibold text-gray-800">Мои курсы</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-gray-50/40">
                    {["Курс", "Просм.", "Покупки", "Конверсия", "Рейтинг", "Завершаемость", "Выручка", ""].map((h) => (
                      <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {data.courses.map((c) => (
                    <tr
                      key={c.course_id}
                      onClick={() => navigate(`/mentor/analytics/courses/${c.course_id}`)}
                      className="hover:bg-blue-50/40 transition-colors cursor-pointer group"
                    >
                      <td className="px-4 py-3.5">
                        <p className="font-medium text-gray-800">{c.title}</p>
                      </td>
                      <td className="px-4 py-3.5 text-gray-700">{c.views}</td>
                      <td className="px-4 py-3.5 text-gray-700">{c.purchases}</td>
                      <td className="px-4 py-3.5">
                        <span className={`font-semibold ${c.conversion_rate >= 10 ? "text-emerald-600" : c.conversion_rate >= 7 ? "text-amber-600" : "text-red-500"}`}>
                          {c.conversion_rate}%
                        </span>
                      </td>
                      <td className="px-4 py-3.5"><Stars rating={c.avg_rating} /></td>
                      <td className="px-4 py-3.5">
                        <div className="flex items-center gap-2">
                          <div className="w-16 h-1.5 bg-gray-100 rounded-full overflow-hidden">
                            <div
                              className={`h-full rounded-full ${c.completion_rate >= 70 ? "bg-emerald-500" : c.completion_rate >= 50 ? "bg-amber-400" : "bg-red-400"}`}
                              style={{ width: `${c.completion_rate}%` }}
                            />
                          </div>
                          <span className="text-xs text-gray-600">{c.completion_rate}%</span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-gray-700 font-medium whitespace-nowrap">
                        {(c.revenue / 1000).toFixed(0)}K сом
                      </td>
                      <td className="px-4 py-3.5">
                        <ChevronRightIcon className="w-4 h-4 text-gray-300 group-hover:text-blue-500 transition-colors" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
